/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";

import { ExportOutlined, PrinterOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  DatePicker,
  Dropdown,
  Layout,
  Modal,
  Progress,
  Rate,
  Space,
  Typography,
} from "antd";
import excel from "xlsx";
import "./style.css";

import Avatar from "antd/lib/avatar/avatar";
import axios from "axios";
import firstTrophyImage from "../../../assets/images/profileTrophies/first_today.png";
import secondTrophyImage from "../../../assets/images/profileTrophies/second.png";
import trophyImage from "../../../assets/images/profileTrophies/tag.png";
import thirdTrophyImage from "../../../assets/images/profileTrophies/third.png";
import { Env, PrintFonts } from "./../../../styles";
import {
  computeDisciplineStarRatio,
  disciplineStarRatioToRateValue,
} from "../../../utilites/disciplineStar";
import AppTable from "../../atoms/AppTable";
const { Text } = Typography;

/**
 * Seconds since midnight for sorting.
 * Handles "HH:mm:ss", "h:mm:ss AM/PM", "YYYY-MM-DD HH:mm:ss", ISO "…T…Z", etc.
 */
function parseAttendanceTimeString(timeString) {
  if (timeString == null || timeString === "") return 0;
  let s = String(timeString).trim().replace(/Z$/i, "");

  const fromDateTime = s.match(
    /(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{2}):(\d{2})(\.\d+)?/
  );
  if (fromDateTime) {
    const h = Number(fromDateTime[2]);
    const m = Number(fromDateTime[3]);
    const sec = Number(fromDateTime[4] + (fromDateTime[5] || ""));
    if (!Number.isFinite(h + m + sec)) return 0;
    return h * 3600 + m * 60 + sec;
  }

  const parts = s.split(/\s+/);
  let timePart = parts[0];
  let meridiem = parts[1];
  if (/^\d{4}-\d{2}-\d{2}$/.test(timePart) && parts[1] && /^\d{1,2}:/.test(parts[1])) {
    timePart = parts[1];
    meridiem = parts[2];
  }

  const timeMatch = timePart.match(/^(\d{1,2}):(\d{2})(?::(\d{2})(\.\d+)?)?$/);
  if (!timeMatch) return 0;

  let h = Number(timeMatch[1]);
  const m = Number(timeMatch[2]);
  const sec = Number((timeMatch[3] != null ? timeMatch[3] : "0") + (timeMatch[4] || ""));
  if (!Number.isFinite(h + m + sec)) return 0;

  if (meridiem) {
    const up = String(meridiem).toUpperCase();
    if (up.startsWith("PM") && h < 12) h += 12;
    if (up.startsWith("AM") && h === 12) h = 0;
  }

  return h * 3600 + m * 60 + sec;
}

function attendanceUserKey(id) {
  if (id == null || id === "") return "";
  const n = Number(id);
  return Number.isFinite(n) ? String(n) : String(id);
}

/** دمج سجلات الحضور المتعددة لنفس الموظف في يوم واحد */
function mergeLogsForUser(logs) {
  if (!logs?.length) return null;
  if (logs.length === 1) return logs[0];

  const sorted = [...logs].sort(
    (a, b) =>
      parseAttendanceTimeString(a.attendance_time) -
      parseAttendanceTimeString(b.attendance_time),
  );

  const withFingerprints = sorted.find(
    (log) => log.all_fingerprints || log.fingerprints,
  );
  if (withFingerprints) {
    return {
      ...withFingerprints,
      startLateTime: sorted[0].startLateTime,
    };
  }

  const times = [];
  sorted.forEach((log) => {
    if (log.attendance_time) times.push(String(log.attendance_time).trim());
    if (log.leave_time) times.push(String(log.leave_time).trim());
  });

  return {
    ...sorted[0],
    all_fingerprints: times.join(","),
    extra_attendance: sorted.map((log) => ({
      in: log.attendance_time || null,
      out: log.leave_time || null,
    })),
  };
}

function groupLogsByUser(logsRaw) {
  const grouped = {};
  (logsRaw || []).forEach((item) => {
    const uid = attendanceUserKey(item.user_id);
    if (!grouped[uid]) grouped[uid] = [];
    grouped[uid].push(item);
  });

  const merged = {};
  Object.keys(grouped).forEach((uid) => {
    merged[uid] = mergeLogsForUser(grouped[uid]);
  });
  return merged;
}

function parseFingerprintsString(fpString) {
  if (!fpString) return [];
  const s = String(fpString).trim();
  if (!s) return [];

  if (s.includes("|")) {
    const parts = s.split("|").map((p) => p.trim()).filter(Boolean);
    const intervals = [];
    for (let i = 0; i < parts.length; i += 2) {
      intervals.push({ in: parts[i] || null, out: parts[i + 1] || null });
    }
    return intervals;
  }

  const fpArray = s.split(",");
  const intervals = [];
  for (let i = 0; i < fpArray.length; i += 2) {
    const inn = fpArray[i]?.trim() || null;
    const out = fpArray[i + 1]?.trim() || null;
    if (inn || out) intervals.push({ in: inn, out });
  }
  return intervals;
}

/** مطابقة السجل التفصيلي: attendancelog + attendancelogs لكل فترة */
async function enrichAttendanceFromDetailLog(userId, date, baseAttendance) {
  if (!userId || !date || !baseAttendance) return baseAttendance;
  try {
    const [dayRes, logsRes] = await Promise.all([
      axios.get(
        `${Env.HOST_SERVER_NAME}attendancelog/${userId}/${date}/${date}`,
      ),
      axios.get(
        `${Env.HOST_SERVER_NAME}attendancelogs/${userId}/${date}`,
      ),
    ]);
    const rows = Array.isArray(dayRes.data) ? dayRes.data : [];
    const dayLog = rows.find((row) => row?.date === date) || rows[0] || null;
    const stampLogs = (Array.isArray(logsRes.data) ? logsRes.data : []).filter(
      (log) =>
        log?.attendance_time &&
        (log.type == null || Number(log.type) === 0),
    );
    const stampSegments = buildStampSegmentsFromLogs(stampLogs);

    if (!dayLog && !stampSegments.length) return baseAttendance;

    const extraFromDay = Array.isArray(dayLog?.extra_attendance)
      ? dayLog.extra_attendance
      : baseAttendance.extra_attendance;

    return {
      ...baseAttendance,
      attendance_time:
        dayLog?.attendance_time || baseAttendance.attendance_time,
      leave_time: dayLog?.leave_time || baseAttendance.leave_time,
      workHours: dayLog?.workHours ?? baseAttendance.workHours,
      extra_attendance: attachWorkHoursToSegments(
        extraFromDay,
        stampLogs,
      ),
      stamp_logs: stampLogs,
      stamp_segments:
        stampSegments.length > 0
          ? stampSegments
          : attachWorkHoursToSegments(
              extraFromDay || [
                {
                  in: baseAttendance.attendance_time,
                  out: baseAttendance.leave_time,
                },
              ],
              stampLogs,
            ),
      fingerprints: dayLog?.fingerprints || baseAttendance.fingerprints,
      startLateTime:
        dayLog?.startLateTime ?? baseAttendance.startLateTime,
      attendance_status:
        dayLog?.attendance_status ?? baseAttendance.attendance_status,
      is_late: dayLog?.is_late ?? baseAttendance.is_late,
      late_minutes: dayLog?.late_minutes ?? baseAttendance.late_minutes,
    };
  } catch {
    return baseAttendance;
  }
}

async function enrichAllAttendances(joinedData, date) {
  return Promise.all(
    joinedData.map(async (item) => {
      if (!item.attendance) return item;
      return {
        ...item,
        attendance: await enrichAttendanceFromDetailLog(
          item.user_id,
          date,
          item.attendance,
        ),
      };
    }),
  );
}

/** فترات الدخول/الخروج لعرضها في الجدول والطباعة */
function getAttendanceDisplayIntervals(attendance) {
  if (!attendance) return [{ in: null, out: null, workHours: null }];

  if (Array.isArray(attendance.stamp_segments) && attendance.stamp_segments.length) {
    return attendance.stamp_segments;
  }

  if (
    Array.isArray(attendance.extra_attendance) &&
    attendance.extra_attendance.length > 1
  ) {
    return attachWorkHoursToSegments(
      attendance.extra_attendance.map((seg) => ({
        in: seg?.in || null,
        out: seg?.out || null,
        workHours: seg?.workHours || seg?.workHour || null,
      })),
      attendance.stamp_logs,
    );
  }

  const fromFp = parseFingerprintsString(
    attendance.fingerprints || attendance.all_fingerprints || "",
  );
  if (fromFp.length) {
    return attachWorkHoursToSegments(fromFp, attendance.stamp_logs);
  }

  if (
    Array.isArray(attendance.extra_attendance) &&
    attendance.extra_attendance.length === 1
  ) {
    const seg = attendance.extra_attendance[0];
    return attachWorkHoursToSegments(
      [{ in: seg?.in || null, out: seg?.out || null }],
      attendance.stamp_logs,
    );
  }

  return attachWorkHoursToSegments(
    [
      {
        in: attendance.attendance_time || null,
        out: attendance.leave_time || null,
      },
    ],
    attendance.stamp_logs,
  );
}

function formatStampDisplay(value) {
  if (value == null || value === "") return "-";
  const s = String(value).trim();
  const spaceIdx = s.indexOf(" ");
  if (spaceIdx > 0 && s.length > spaceIdx + 1) {
    return s.slice(spaceIdx + 1);
  }
  return s;
}

function formatWorkHoursDisplay(value) {
  if (value == null || value === "") return "-";
  const s = String(value).trim();
  if (!s || s === "00:00:00") return s === "00:00:00" ? "00:00:00" : "-";
  return s;
}

/** مدة الفترة بين دخول وخروج (HH:mm:ss) */
function computeIntervalWorkHours(inVal, outVal) {
  if (!inVal || !outVal) return null;
  const inSec = parseAttendanceTimeString(inVal);
  const outSec = parseAttendanceTimeString(outVal);
  if (!inSec || !outSec || outSec <= inSec) return null;
  const diff = outSec - inSec;
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function intervalStampKey(inVal, outVal) {
  return `${parseAttendanceTimeString(inVal)}|${parseAttendanceTimeString(outVal ?? "")}`;
}

function attachWorkHoursToSegments(segments, stampLogs) {
  const workByKey = {};
  (stampLogs || []).forEach((log) => {
    if (!log?.attendance_time) return;
    const wh = log.workHour || log.workHours || null;
    workByKey[intervalStampKey(log.attendance_time, log.leave_time)] = wh;
  });

  return (segments || []).map((seg) => {
    const wh =
      seg?.workHours ||
      seg?.workHour ||
      workByKey[intervalStampKey(seg?.in, seg?.out)] ||
      computeIntervalWorkHours(seg?.in, seg?.out);
    return { ...seg, workHours: wh };
  });
}

function buildStampSegmentsFromLogs(stampLogs) {
  return [...(stampLogs || [])]
    .filter((log) => log?.attendance_time)
    .sort(
      (a, b) =>
        parseAttendanceTimeString(a.attendance_time) -
        parseAttendanceTimeString(b.attendance_time),
    )
    .map((log) => ({
      in: log.attendance_time,
      out: log.leave_time || null,
      workHours:
        log.workHour ||
        log.workHours ||
        computeIntervalWorkHours(log.attendance_time, log.leave_time),
    }));
}

function expandRowsForTable(items) {
  const rows = [];
  let rank = 0;
  (items || []).forEach((item) => {
    rank += 1;
    const intervals = getAttendanceDisplayIntervals(item.attendance);
    intervals.forEach((interval, i) => {
      rows.push({
        ...item,
        _rowKey: `${item.user_id}-${i}`,
        _displayRank: i === 0 ? rank : null,
        _isExtraInterval: i > 0,
        _stampIn: interval.in,
        _stampOut: interval.out,
        _intervalWorkHours: interval.workHours,
      });
    });
  });
  return rows;
}

export default function generalTable(props) {
  const [cookies] = useCookies(["userId"]);
  const loadRequestRef = useRef(0);

  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [data, setData] = useState([]);
  const [abData, setAbData] = useState([]);
  const [lateData, setLateData] = useState([]);
  const [type, setType] = useState(null);

  const [attAvg, setAttAvg] = useState(0);
  const [abCount, setAbCount] = useState(0);
  const [lateCount, setLateCount] = useState(0);
  const [timelyCount, setTimelyCount] = useState(0);
  const [timelyData, setTimelyData] = useState([]);

  const [load, setLoad] = useState(true);
  const [today, setToday] = useState(new Date().toISOString().split("T")[0]);
  const [starList, setStarList] = useState([]);
  const [showTrophyModal, setShowTrophyModal] = useState(false);
  const [userRank, setUserRank] = useState(null);

  const id = cookies.user;
  const changeDate = (all, date) => {
    setToday(date);
  };

  // دالة لتحديد الصورة حسب الترتيب
  const getTrophyImage = (rank) => {
    switch (rank) {
      case 1:
        return firstTrophyImage;
      case 2:
        return secondTrophyImage;
      case 3:
        return thirdTrophyImage;
      default:
        return trophyImage;
    }
  };
  function onLoad() {
    document.getElementById("elemID").scrollIntoView({ behavior: "smooth" });
  }

  const handleMenuClick = (e) => {
    switch (e.key) {
      case "1":
        printReport("abs-report");
        break;
      case "2":
        printReport("late-report");
        break;
      case "3":
        printReport("timely-report");
        break;
      default:
        break;
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const requestId = ++loadRequestRef.current;
    setLoad(true);

    const userTypeReq = props.user?.id
      ? axios.get(Env.HOST_SERVER_NAME + "user-type/" + props.user.id).catch((e) => {
          console.log(e);
          return { data: null };
        })
      : Promise.resolve({ data: null });

    Promise.all([
      userTypeReq,
      axios.get(Env.HOST_SERVER_NAME + "all-users-events-log/" + today),
    ])
      .then(async ([typeRes, response]) => {
        if (requestId !== loadRequestRef.current) return;
        if (typeRes?.data != null) {
          setType(typeRes.data);
        }
        const logsByUser = groupLogsByUser(response.data["logs_test"] || []);

        const joinedData = response.data["users"].map((user) => ({
          ...user,
          attendance: logsByUser[attendanceUserKey(user.user_id)] || null,
        }));

        const enrichedData = await enrichAllAttendances(joinedData, today);
        if (requestId !== loadRequestRef.current) return;

        const orderDataByAttendance = (rows) =>
          [...rows].sort((a, b) => {
            if (a.attendance === null) return 1;
            if (b.attendance === null) return -1;
            const timeA = parseAttendanceTimeString(a.attendance?.attendance_time);
            const timeB = parseAttendanceTimeString(b.attendance?.attendance_time);
            if (timeA !== timeB) return timeA - timeB;
            return (
              (Number(a.user_id) || 0) - (Number(b.user_id) || 0)
            );
          });

        const data = orderDataByAttendance(enrichedData);

        setData(data.filter((item) => item.attendance != null));

        const abdata = data.filter((item) => item.attendance == null);
        setAbData(abdata);
        const isLate = (attendance) => {
          if (!attendance) return false;
          if (attendance.attendance_status != null) {
            return String(attendance.attendance_status).toLowerCase() === "late";
          }
          if (attendance.late_minutes != null) {
            return Number(attendance.late_minutes) > 0;
          }
          if (attendance.is_late != null) return Number(attendance.is_late) === 1;
          return Number(attendance.startLateTime) < 0;
        };
        const latedata = data.filter((item) => isLate(item.attendance));
        setLateData(latedata);
        const timelydata = data.filter((item) => item.attendance && !isLate(item.attendance));
        setTimelyData(timelydata);

        setAttAvg(
          Math.round(
            (data.filter((item) => item.attendance != null).length /
              response.data["users"].length) *
            100
          )
        );
        setAbCount(abdata.length);
        setLateCount(latedata.length);
        setTimelyCount(timelydata.length);

        const workDaysCount =
          Number(response.data.count?.[0]?.count) > 0
            ? Number(response.data.count[0].count)
            : 30;
        const stars = [];
        response.data["lists"].forEach(function (e) {
          const ratio = computeDisciplineStarRatio(e, workDaysCount);
          stars.push({
            user_id: e.user_id,
            star: disciplineStarRatioToRateValue(ratio),
          });
        });
        setStarList(stars);

        // فحص ترتيب المستخدم الحالي
        const currentUserIndex = data.findIndex(
          (item) => item.user_id === id.user_id
        );
        if (currentUserIndex !== -1 && currentUserIndex < 3) {
          setUserRank(currentUserIndex + 1);
          setShowTrophyModal(true);
        }

        setLoad(false);
      })
      .catch(function (error) {
        if (requestId !== loadRequestRef.current) return;
        console.log(error);
        setLoad(false);
      });
  }, [today, id.user_id, props.user?.id]);

  const tableRows = useMemo(() => expandRowsForTable(data), [data]);

  const columns = [
    {
      title: "ت",
      key: "avatar",
      dataIndex: "avatar",
      ellipsis: false,
      width: "70px",
      render: (_, record) => {
        return <Text>{record._isExtraInterval ? "-" : record._displayRank}</Text>;
      },
    },
    {
      title: "وقت الحضور",
      dataIndex: "attendance",
      key: "attendance_in",
      sorter: (a, b) =>
        (a._stampIn
          ? parseAttendanceTimeString(a._stampIn)
          : Number.POSITIVE_INFINITY) -
        (b._stampIn
          ? parseAttendanceTimeString(b._stampIn)
          : Number.POSITIVE_INFINITY),
      sortOrder: sortedInfo.columnKey === "attendance_in" && sortedInfo.order,
      ellipsis: false,
      width: "120px",
      render: (_, record) => <Text>{formatStampDisplay(record._stampIn)}</Text>,
    },
    {
      title: "وقت الانصراف",
      dataIndex: "attendance",
      key: "attendance_out",
      ellipsis: false,
      width: "120px",
      render: (_, record) => <Text>{formatStampDisplay(record._stampOut)}</Text>,
    },
    {
      title: "الدوام الفعلي",
      dataIndex: "attendance",
      key: "interval_work_hours",
      ellipsis: false,
      width: "110px",
      render: (_, record) => (
        <Text>{formatWorkHoursDisplay(record._intervalWorkHours)}</Text>
      ),
    },
    {
      title: "الاسم",
      dataIndex: "user_name",
      key: "user_name",
      ellipsis: false,
      width: "180px",
      render: (user_name, record) =>
        record._isExtraInterval ? (
          <Text>-</Text>
        ) : (
          <div>
            <Avatar src={Env.HOST_SERVER_STORAGE + record.avatar}></Avatar>
            <Text style={{ marginRight: "10px" }}>{user_name}</Text>
          </div>
        ),
    },
    {
      title: "تقييم الانضباط",
      dataIndex: "user_id",
      key: "user_id",
      ellipsis: false,
      width: "180px",
      render: (user_id, record) =>
        record._isExtraInterval ? (
          <Text>-</Text>
        ) : (
          <Rate
            style={{ textAlign: "center", marginBottom: "5px" }}
            disabled
            allowHalf
            value={
              starList?.filter(function (e) {
                return e.user_id == user_id;
              })[0]?.star
            }
          />
        ),
    },
    {
      title: "الإدارة",
      dataIndex: "department",
      key: "department",
      sorter: (a, b) => a.department - b.department,
      sortOrder: sortedInfo.columnKey === "department" && sortedInfo.order,
      ellipsis: true,
      render: (department, record) => {
        return <Text>{record._isExtraInterval ? "-" : department}</Text>;
      },
    },
    {
      title: "الوظيفة",
      dataIndex: "job",
      key: "job",
      ellipsis: true,
      render: (job, record) => {
        return <Text>{record._isExtraInterval ? "-" : job}</Text>;
      },
    },
  ];

  const printReport = (element) => {
    var report = document.getElementById(element);
    //var report=document.body;
    var mywindow = window.open("");
    mywindow.document.write(
      "<html><head><title></title> <style>" +
      PrintFonts.getPrintFontsCSS() +
      "body{font-size:12px;margin:0} " +
      "</style><style type='text/css' media='print'>@page { size: portrait; }</style>"
    );
    mywindow.document.write('</head><body dir="rtl" style="font-size:12px;" >');
    mywindow.document.write(report.innerHTML);
    mywindow.document.write("</body></html>");

    // mywindow.document.close();
    mywindow.onload = function () {
      // wait until all resources loaded
      mywindow.focus(); // necessary for IE >= 10
      mywindow.print(); // change window to mywindow
      // mywindow.close(); // change window to mywindow
    };
  };

  const exportToExcel = (type, fn, dl) => {
    var elt = document.getElementsByClassName("print-table")[0];
    if (elt) {
      var wb = excel.utils.table_to_book(elt, { sheet: "sheet1" });
      return dl
        ? excel.write(wb, { bookType: type, bookSST: true, type: "base64" })
        : excel.writeFile(
          wb,
          fn ||
          "حافظة دوام ليوم " +
          days[new Date(today).getDay()] +
          " الموافق " +
          today +
          "." +
          (type || "xlsx")
        );
    }
  };

  const items = [
    {
      label: "طباعة الغائبين",
      key: "1",
    },
    {
      label: "طباعة المتأخرين",
      key: "2",
    },
    {
      label: "طباعة المنضبطين",
      key: "3",
    },
  ];

  var rank = 1;
  var trank = 1;
  var index = 1;
  var lindex = 1;

  var days = [
    "الأحد",
    "الاثنين",
    "الثلاثاء",
    "الأربعاء",
    "الخميس",
    "الجمعة",
    "السبت",
  ];

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  return (
    <Layout>
      <Card bodyStyle={{ padding: "10px" }}>
        <div className="generalHeader">
          <div className="generalData">
            <div className="attPer">
              <span>
                <Progress
                  type="circle"
                  percent={Math.round(attAvg)}
                  width={window.innerWidth <= 760 ? 50 : 70}
                  style={{ marginLeft: "5px", display: "inline-block" }}
                />
              </span>
              <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                  paddingTop: "10px",
                  marginRight: "5px",
                }}
              >
                <div style={{ marginBottom: "5px" }}>
                  الحاضرون : <span>{data.length}</span>{" "}
                </div>
                <div>
                  الغائبون : <span>{abCount}</span>{" "}
                </div>
              </span>
            </div>
            <div className="disPer">
              <span>
                <Progress
                  type="circle"
                  percent={Math.round((timelyCount / data.length) * 100)}
                  width={window.innerWidth <= 760 ? 50 : 70}
                  style={{ marginLeft: "5px", display: "inline-block" }}
                />
              </span>
              <span
                style={{
                  display: "flex",
                  flexDirection: "column",
                  paddingTop: "10px",
                  marginRight: "5px",
                }}
              >
                <div style={{ marginBottom: "5px" }}>
                  المنضبطون : <span>{timelyCount} </span>
                </div>
                <div>
                  المتأخرون : <span>{lateCount}</span>
                </div>
              </span>
            </div>
          </div>
          {type && props.user.role_id == 1 ? (
            <div className="generalOper">
              <div
                className="date"
                style={{ marginBottom: "10px", marginLeft: "5px" }}
              >
                <span>اختر يومًا : </span>{" "}
                <DatePicker
                  needConfirm={false}
                  inputReadOnly={window.innerWidth <= 760}
                  onChange={changeDate}
                />
              </div>
              <Button
                style={{
                  display: "block",
                  marginLeft: "5px",
                  marginBottom: "10px",
                }}
                onClick={function () {
                  exportToExcel("xlsx");
                }}
                type="primary"
              >
                <ExportOutlined />
              </Button>

              <Space style={{ display: "inline-block" }}>
                <Dropdown.Button
                  menu={menuProps}
                  trigger={["click"]}
                  style={{ backgroundColor: "#0972B6" }}
                  onClick={function () {
                    printReport("att-report");
                  }}
                  type="primary"
                >
                  <PrinterOutlined /> طباعة الحاضرين
                </Dropdown.Button>
              </Space>
            </div>
          ) : (
            <></>
          )}
        </div>
        <AppTable
          className="genTable"
          rowKey="_rowKey"
          onRow={(record, rowIndex) => {
            if (record.user_id == id.user_id) {
              return {
                style: { backgroundColor: "#D3D3D3" },
                onLoad: (event) => {
                  if (!record._isExtraInterval) onLoad();
                },
                id: record._isExtraInterval ? undefined : "elemID",
              };
            }
          }}
          scroll={{ x: "1000px" }}
          loading={load}
          columns={columns}
          dataSource={tableRows}
          onChange={handleChange}
        />
      </Card>
      <div id="att-report" style={{ display: "none" }}>
        <div
          style={{
            direction: "rtl",
            fontSize: "12px",
            fontFamily: "Tajawal, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            margin: "0",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "row",
              borderColor: "#000",
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
            }}
          >
            <div style={{ flex: 1 }}>
              <img
                loading="eager"
                style={{ width: "280px" }}
                src={
                  Env.HOST_SERVER_STORAGE +
                  props.setting.filter((item) => item.key == "admin.logo")[0]
                    ?.value
                }
              />
            </div>
            <div
              style={{
                height: "100%",
                fontSize: "11px",
                textAlign: "center",
                flex: 1,
              }}
            >
              <div style={{ height: "50px" }}></div>
              <h1
                style={{
                  textAlign: "center",
                  fontSize: " 18px",
                  marginBottom: " 5px",
                  margin: "0",
                }}
              >
                حافظة دوام الموظفين
              </h1>
              <h2
                style={{
                  textAlign: "center",
                  fontSize: " 14px",
                  fontWeight: " 200",
                  margin: "0",
                }}
              >
                ليوم {days[new Date(today).getDay()]} الموافق {today}
              </h2>
            </div>
            <div
              style={{
                padding: "20px 30px 20px 10px",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "right",
                width: "100%",
                flex: 1,
              }}
            >
              <div>نسبة الحضور: {Math.round(attAvg) + "%"}</div>
              <div>عدد المنضبطين: {timelyCount}</div>
              <div>عدد المتأخرين: {lateCount}</div>
              <div>عدد المتغيبين: {abCount}</div>
            </div>
          </header>
          <div>
            <table
              className="print-table"
              style={{
                fontSize: "11px",
                width: " 100%",
                textAlign: " center",
                marginTop: " 20px",
              }}
            >
              <thead>
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <th style={{ fontWeight: "100" }}>م</th>
                  <th style={{ fontWeight: "100", fontSize: "8px" }}>
                    الرقم الوظيفي
                  </th>
                  <th style={{ fontWeight: "100" }}>الاسم</th>
                  <th style={{ fontWeight: "100" }}>الإدارة</th>
                  <th style={{ fontWeight: "100" }}>الوظيفة</th>
                  <th style={{ fontWeight: "100" }}>زمن الحضور</th>
                  <th style={{ fontWeight: "100" }}>زمن الانصراف</th>
                  <th style={{ fontWeight: "100" }}>الدوام الفعلي</th>
                </tr>
              </thead>
              <tbody>
                {data.flatMap((item, idx) => {
                  const displayIntervals = getAttendanceDisplayIntervals(
                    item.attendance,
                  );
                  const firstInterval = displayIntervals[0];
                  const extraIntervals = displayIntervals.slice(1);

                  const baseRow = (
                    <tr
                      key={`base-${item.user_id}`}
                      style={{
                        height: " 30px",
                        backgroundColor:
                          idx % 2 !== 0 ? "#e6e6e6" : "#fff",
                      }}
                    >
                      <td>{rank++}</td>
                      <td>{item.user_id}</td>
                      <td>{item.fullname}</td>
                      <td style={{ fontSize: "8px" }}>{item.department}</td>
                      <td style={{ fontSize: "8px" }}>{item.job}</td>
                      <td>{formatStampDisplay(firstInterval.in)}</td>
                      <td>{formatStampDisplay(firstInterval.out)}</td>
                      <td>{formatWorkHoursDisplay(firstInterval.workHours)}</td>
                    </tr>
                  );

                  const extraRows = extraIntervals.map((interval, i) => (
                    <tr
                      key={`extra-${item.user_id}-${i}`}
                      style={{
                        height: " 25px",
                        backgroundColor: (idx + i + 1) % 2 !== 0 ? "#e6e6e6" : "#fff",
                      }}
                    >
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{formatStampDisplay(interval.in)}</td>
                      <td>{formatStampDisplay(interval.out)}</td>
                      <td>{formatWorkHoursDisplay(interval.workHours)}</td>
                    </tr>
                  ));

                  return [baseRow, ...extraRows];
                })}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              marginTop: "20px",
              textAlign: "center",
            }}
          >
            <div style={{ width: "50%", fontWeight: "900" }}>المختص</div>
            <div style={{ width: "50%", fontWeight: "900" }}>مدير الشؤون</div>
          </div>
          <div
            style={{
              marginTop: " 20px",
              width: "85%",
              backgroundColor: "#e6e6e61",
              padding: "5px 0",
              borderTopLeftRadius: " 5px",
              borderBottomLeftRadius: " 5px",
            }}
          >
            <div
              style={{
                backgroundColor: " #0972B6",
                width: " 95%",
                height: " 15px",
                borderTopLeftRadius: " 5px",
                borderBottomLeftRadius: " 5px",
                color: " #fff",
                paddingRight: " 20px",
              }}
            >
              نظام دوام | {new Date().toLocaleString("en-IT")}{" "}
            </div>
          </div>
        </div>
      </div>

      <div id="abs-report" style={{ display: "none" }}>
        <div
          style={{
            direction: "rtl",
            fontSize: "12px",
            fontFamily: "Tajawal, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            margin: "0",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "row",
              borderColor: "#000",
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
            }}
          >
            <div style={{ flex: 1 }}>
              <img
                loading="eager"
                style={{ width: "280px" }}
                src={
                  Env.HOST_SERVER_STORAGE +
                  props.setting.filter((item) => item.key == "admin.logo")[0]
                    ?.value
                }
              />
            </div>
            <div
              style={{
                height: "100%",
                fontSize: "11px",
                textAlign: "center",
                flex: 1,
              }}
            >
              <div style={{ height: "50px" }}></div>
              <h1
                style={{
                  textAlign: "center",
                  fontSize: " 18px",
                  marginBottom: " 5px",
                  margin: "0",
                }}
              >
                أسماء الموظفين الغائبين
              </h1>
              <h2
                style={{
                  textAlign: "center",
                  fontSize: " 14px",
                  fontWeight: " 200",
                  margin: "0",
                }}
              >
                ليوم {days[new Date(today).getDay()]} الموافق {today}
              </h2>
            </div>
            <div
              style={{
                padding: "20px 30px 20px 10px",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "right",
                width: "100%",
                flex: 1,
              }}
            >
              <div>نسبة الحضور: {Math.round(attAvg) + "%"}</div>
              <div>عدد المنضبطين: {timelyCount}</div>
              <div>عدد المتأخرين: {lateCount}</div>
              <div>عدد المتغيبين: {abCount}</div>
            </div>
          </header>
          <div>
            <table
              className="print-table"
              style={{
                fontSize: "11px",
                width: " 100%",
                textAlign: " center",
                marginTop: " 20px",
              }}
            >
              <thead>
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <th style={{ fontWeight: "100" }}>م</th>
                  <th style={{ fontWeight: "100", fontSize: "8px" }}>
                    الرقم الوظيفي
                  </th>
                  <th style={{ fontWeight: "100" }}>الاسم</th>
                  <th style={{ fontWeight: "100" }}>الإدارة</th>
                  <th style={{ fontWeight: "100" }}>الوظيفة</th>
                  <th style={{ fontWeight: "100" }}>ملاحظات</th>
                </tr>
              </thead>
              <tbody>
                {abData?.map((item) => (
                  <tr
                    style={{
                      height: " 30px",
                      backgroundColor:
                        abData.indexOf(item) % 2 != 0 ? "#e6e6e6" : "#fff",
                    }}
                  >
                    <td>{index++}</td>
                    <td>{item.user_id}</td>
                    <td>{item.fullname}</td>
                    <td style={{ fontSize: "8px" }}>{item.department}</td>
                    <td style={{ fontSize: "8px" }}>{item.job}</td>
                    <td> </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              marginTop: "20px",
              textAlign: "center",
            }}
          >
            <div style={{ width: "50%", fontWeight: "900" }}>المختص</div>
            <div style={{ width: "50%", fontWeight: "900" }}>مدير الشؤون</div>
          </div>
          <div
            style={{
              marginTop: " 20px",
              width: "85%",
              backgroundColor: "#e6e6e61",
              padding: "5px 0",
              borderTopLeftRadius: " 5px",
              borderBottomLeftRadius: " 5px",
            }}
          >
            <div
              style={{
                backgroundColor: " #0972B6",
                width: " 95%",
                height: " 15px",
                borderTopLeftRadius: " 5px",
                borderBottomLeftRadius: " 5px",
                color: " #fff",
                paddingRight: " 20px",
              }}
            >
              نظام دوام | {new Date().toLocaleString("en-IT")}{" "}
            </div>
          </div>
        </div>
      </div>
      <div id="late-report" style={{ display: "none" }}>
        <div
          style={{
            direction: "rtl",
            fontSize: "12px",
            fontFamily: "Tajawal, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            margin: "0",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "row",
              borderColor: "#000",
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
            }}
          >
            <div style={{ flex: 1 }}>
              <img
                loading="eager"
                style={{ width: "280px" }}
                src={
                  Env.HOST_SERVER_STORAGE +
                  props.setting.filter((item) => item.key == "admin.logo")[0]
                    ?.value
                }
              />
            </div>
            <div
              style={{
                height: "100%",
                fontSize: "11px",
                textAlign: "center",
                flex: 1,
              }}
            >
              <div style={{ height: "50px" }}></div>
              <h1
                style={{
                  textAlign: "center",
                  fontSize: " 18px",
                  marginBottom: " 5px",
                  margin: "0",
                }}
              >
                أسماء الموظفين المتأخرين
              </h1>
              <h2
                style={{
                  textAlign: "center",
                  fontSize: " 14px",
                  fontWeight: " 200",
                  margin: "0",
                }}
              >
                ليوم {days[new Date(today).getDay()]} الموافق {today}
              </h2>
            </div>
            <div
              style={{
                padding: "20px 30px 20px 10px",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "right",
                width: "100%",
                flex: 1,
              }}
            >
              <div>نسبة الحضور: {Math.round(attAvg) + "%"}</div>
              <div>عدد المنضبطين: {timelyCount}</div>
              <div>عدد المتأخرين: {lateCount}</div>
              <div>عدد المتغيبين: {abCount}</div>
            </div>
          </header>
          <div>
            <table
              className="print-table"
              style={{
                fontSize: "11px",
                width: " 100%",
                textAlign: " center",
                marginTop: " 20px",
              }}
            >
              <thead>
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <th style={{ fontWeight: "100" }}>م</th>
                  <th style={{ fontWeight: "100", fontSize: "8px" }}>
                    الرقم الوظيفي
                  </th>
                  <th style={{ fontWeight: "100" }}>الاسم</th>
                  <th style={{ fontWeight: "100" }}>الإدارة</th>
                  <th style={{ fontWeight: "100" }}>الوظيفة</th>
                  <th style={{ fontWeight: "100" }}>زمن الحضور</th>
                  <th style={{ fontWeight: "100" }}>زمن الانصراف</th>
                  <th style={{ fontWeight: "100" }}>وقت التأخر </th>
                </tr>
              </thead>
              <tbody>
                {lateData.flatMap((item, idx) => {
                  const displayIntervals = getAttendanceDisplayIntervals(
                    item.attendance,
                  );
                  const firstInterval = displayIntervals[0];
                  const extraIntervals = displayIntervals.slice(1);

                  const baseRow = (
                    <tr
                      key={`base-late-${item.user_id}`}
                      style={{
                        height: " 30px",
                        backgroundColor:
                          idx % 2 !== 0 ? "#e6e6e6" : "#fff",
                      }}
                    >
                      <td>{lindex++}</td>
                      <td>{item.user_id}</td>
                      <td>{item.fullname}</td>
                      <td style={{ fontSize: "8px" }}>{item.department}</td>
                      <td style={{ fontSize: "8px" }}>{item.job}</td>
                      <td>{formatStampDisplay(firstInterval.in)}</td>
                      <td>{formatStampDisplay(firstInterval.out)}</td>
                      <td>
                        {Math.floor(item.attendance?.startLateTime * -1)} دقيقة
                      </td>
                    </tr>
                  );

                  const extraRows = extraIntervals.map((interval, i) => (
                    <tr
                      key={`extra-late-${item.user_id}-${i}`}
                      style={{
                        height: " 25px",
                        backgroundColor: (idx + i + 1) % 2 !== 0 ? "#e6e6e6" : "#fff",
                      }}
                    >
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{formatStampDisplay(interval.in)}</td>
                      <td>{formatStampDisplay(interval.out)}</td>
                      <td>{formatWorkHoursDisplay(interval.workHours)}</td>
                    </tr>
                  ));

                  return [baseRow, ...extraRows];
                })}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              marginTop: "20px",
              textAlign: "center",
            }}
          >
            <div style={{ width: "50%", fontWeight: "900" }}>المختص</div>
            <div style={{ width: "50%", fontWeight: "900" }}>مدير الشؤون</div>
          </div>
          <div
            style={{
              marginTop: " 20px",
              width: "85%",
              backgroundColor: "#e6e6e61",
              padding: "5px 0",
              borderTopLeftRadius: " 5px",
              borderBottomLeftRadius: " 5px",
            }}
          >
            <div
              style={{
                backgroundColor: " #0972B6",
                width: " 95%",
                height: " 15px",
                borderTopLeftRadius: " 5px",
                borderBottomLeftRadius: " 5px",
                color: " #fff",
                paddingRight: " 20px",
              }}
            >
              نظام دوام | {new Date().toLocaleString("en-IT")}{" "}
            </div>
          </div>
        </div>
      </div>

      <div id="timely-report" style={{ display: "none" }}>
        <div
          style={{
            direction: "rtl",
            fontSize: "12px",
            fontFamily: "Tajawal, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
            margin: "0",
          }}
        >
          <header
            style={{
              display: "flex",
              flexDirection: "row",
              borderColor: "#000",
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
            }}
          >
            <div style={{ flex: 1 }}>
              <img
                loading="eager"
                style={{ width: "280px" }}
                src={
                  Env.HOST_SERVER_STORAGE +
                  props.setting.filter((item) => item.key == "admin.logo")[0]
                    ?.value
                }
              />
            </div>
            <div
              style={{
                height: "100%",
                fontSize: "11px",
                textAlign: "center",
                flex: 1,
              }}
            >
              <div style={{ height: "50px" }}></div>
              <h1
                style={{
                  textAlign: "center",
                  fontSize: " 18px",
                  marginBottom: " 5px",
                  margin: "0",
                }}
              >
                حافظة دوام الموظفين المنضبطين
              </h1>
              <h2
                style={{
                  textAlign: "center",
                  fontSize: " 14px",
                  fontWeight: " 200",
                  margin: "0",
                }}
              >
                ليوم {days[new Date(today).getDay()]} الموافق {today}
              </h2>
            </div>
            <div
              style={{
                padding: "20px 30px 20px 10px",
                fontWeight: "600",
                fontSize: "14px",
                textAlign: "right",
                width: "100%",
                flex: 1,
              }}
            >
              <div>نسبة الحضور: {Math.round(attAvg) + "%"}</div>
              <div>عدد المنضبطين: {timelyCount}</div>
              <div>عدد المتأخرين: {lateCount}</div>
              <div>عدد المتغيبين: {abCount}</div>
            </div>
          </header>
          <div>
            <table
              className="print-table"
              style={{
                fontSize: "11px",
                width: " 100%",
                textAlign: " center",
                marginTop: " 20px",
              }}
            >
              <thead>
                <tr
                  style={{
                    color: "#fff",
                    backgroundColor: "#0972B6",
                    height: "30px",
                  }}
                >
                  <th style={{ fontWeight: "100" }}>م</th>
                  <th style={{ fontWeight: "100", fontSize: "8px" }}>
                    الرقم الوظيفي
                  </th>
                  <th style={{ fontWeight: "100" }}>الاسم</th>
                  <th style={{ fontWeight: "100" }}>الإدارة</th>
                  <th style={{ fontWeight: "100" }}>الوظيفة</th>
                  <th style={{ fontWeight: "100" }}>زمن الحضور</th>
                  <th style={{ fontWeight: "100" }}>زمن الانصراف</th>
                  <th style={{ fontWeight: "100" }}>الدوام الفعلي</th>
                </tr>
              </thead>
              <tbody>
                {timelyData?.flatMap((item, idx) => {
                  const displayIntervals = getAttendanceDisplayIntervals(
                    item.attendance,
                  );
                  const firstInterval = displayIntervals[0];
                  const extraIntervals = displayIntervals.slice(1);

                  const baseRow = (
                    <tr
                      key={`base-timely-${item.user_id}`}
                      style={{
                        height: " 30px",
                        backgroundColor:
                          idx % 2 !== 0 ? "#e6e6e6" : "#fff",
                      }}
                    >
                      <td>{trank++}</td>
                      <td>{item.user_id}</td>
                      <td>{item.fullname}</td>
                      <td style={{ fontSize: "8px" }}>{item.department}</td>
                      <td style={{ fontSize: "8px" }}>{item.job}</td>
                      <td>{formatStampDisplay(firstInterval.in)}</td>
                      <td>{formatStampDisplay(firstInterval.out)}</td>
                      <td>{formatWorkHoursDisplay(firstInterval.workHours)}</td>
                    </tr>
                  );

                  const extraRows = extraIntervals.map((interval, i) => (
                    <tr
                      key={`extra-timely-${item.user_id}-${i}`}
                      style={{
                        height: " 25px",
                        backgroundColor: (idx + i + 1) % 2 !== 0 ? "#e6e6e6" : "#fff",
                      }}
                    >
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{"-"}</td>
                      <td>{formatStampDisplay(interval.in)}</td>
                      <td>{formatStampDisplay(interval.out)}</td>
                      <td>{formatWorkHoursDisplay(interval.workHours)}</td>
                    </tr>
                  ));

                  return [baseRow, ...extraRows];
                })}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              marginTop: "20px",
              textAlign: "center",
            }}
          >
            <div style={{ width: "50%", fontWeight: "900" }}>المختص</div>
            <div style={{ width: "50%", fontWeight: "900" }}>مدير الشؤون</div>
          </div>
          <div
            style={{
              marginTop: " 20px",
              width: "85%",
              backgroundColor: "#e6e6e61",
              padding: "5px 0",
              borderTopLeftRadius: " 5px",
              borderBottomLeftRadius: " 5px",
            }}
          >
            <div
              style={{
                backgroundColor: " #0972B6",
                width: " 95%",
                height: " 15px",
                borderTopLeftRadius: " 5px",
                borderBottomLeftRadius: " 5px",
                color: " #fff",
                paddingRight: " 20px",
              }}
            >
              نظام دوام | {new Date().toLocaleString("en-IT")}{" "}
            </div>
          </div>
        </div>
      </div>

      {/* نافذة التكريم */}
      <Modal
        open={showTrophyModal}
        onCancel={() => setShowTrophyModal(false)}
        footer={null}
        centered
        width={380}
        className="trophy-modal-custom"
        closable={false}
        maskStyle={{
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div className="trophy-modal-container">
          <div className="trophy-modal-content">
            <div className="trophy-modal-image-container">
              <img
                src={getTrophyImage(userRank)}
                alt="تكريم"
                className="trophy-modal-image"
              />
              <div className="trophy-modal-glow"></div>
            </div>

            <div className="trophy-modal-text">
              <h2 className="trophy-modal-title">
                أحسنت{" "}
                {data.find((item) => item.user_id === id.user_id)?.user_name}!
              </h2>
              <p className="trophy-modal-subtitle">
                أنت {userRank === 1 ? "أول" : userRank === 2 ? "ثاني" : "ثالث"}{" "}
                الحاضرين اليوم
              </p>
            </div>

            <Button
              className="trophy-modal-button"
              onClick={() => setShowTrophyModal(false)}
            >
              شكراً لك
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
}
